import { useEffect, useMemo, useState } from "react";
import { Avatar, Button, Card, Empty, Input, Modal, Spin, Tag } from "antd";
import {
  CheckOutlined,
  LoadingOutlined,
  SearchOutlined,
  TeamOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import apiClient from "../../actions/api";
import { getCountryName } from "../../config/countries";
import openNotification from "../../components/Notifications/openNotification";
import "../../style/user/teammates.scss";

const TeamMates = ({ title }) => {
  document.title = title;
  const isAdmin = Number(useSelector((state) => state.auth.user?.role)) === 1;
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteQuery, setInviteQuery] = useState("");
  const [inviteResults, setInviteResults] = useState([]);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [invitingId, setInvitingId] = useState(null);
  const [invitedIds, setInvitedIds] = useState([]);

  const loadGroups = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/teams");
      setGroups(data.groups || []);
    } catch (error) {
      openNotification(
        "error",
        "Unable to load team",
        error.response?.data?.errors?.[0]?.msg || "Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGroups();
  }, []);

  const searchUsers = async () => {
    if (inviteQuery.trim().length < 2) return;
    setInviteLoading(true);
    try {
      const { data } = await apiClient.get("/teams/search", {
        params: { q: inviteQuery.trim() },
      });
      setInviteResults(data || []);
    } catch (error) {
      openNotification(
        "error",
        "Search failed",
        error.response?.data?.errors?.[0]?.msg || "Please try again.",
      );
    } finally {
      setInviteLoading(false);
    }
  };

  const inviteUser = async (user) => {
    setInvitingId(user._id);
    try {
      await apiClient.post("/teams/invite", { userId: user._id });
      openNotification(
        "success",
        "Invitation sent",
        `${user.name} will see it in notifications.`,
      );
      setInvitedIds((current) => [...current, user._id]);
    } catch (error) {
      openNotification(
        "error",
        "Unable to invite",
        error.response?.data?.errors?.[0]?.msg || "Please try again.",
      );
    } finally {
      setInvitingId(null);
    }
  };

  const visibleGroups = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    return groups.map((group) => ({
      ...group,
      members: (group.members || []).filter(
        (member) =>
          !query ||
          `${member.name} ${member.email} ${member.phone || ""} ${getCountryName(member.job_type)}`
            .toLowerCase()
            .includes(query),
      ),
    }));
  }, [groups, searchText]);
  const memberCount = groups.reduce(
    (total, group) => total + (group.members?.length || 0),
    0,
  );

  return (
    <div className="teammateSection">
      <div className="teammate-heading">
        <div>
          <h2 className="title">
            <span className="teammate-title-icon">
              <TeamOutlined />
            </span>
            {isAdmin ? "Groups" : "Teammates"}
          </h2>
          <p>
            {isAdmin
              ? "Review Professional groups, their owners, and teammates."
              : "Manage the people collaborating in your Professional group."}
          </p>
        </div>
        <div className="teammate-heading-actions">
          <Tag className="teammate-count">{memberCount} members</Tag>
          {!isAdmin && (
            <Button
              type="primary"
              icon={<UserAddOutlined />}
              onClick={() => setInviteOpen(true)}
            >
              Invite team member
            </Button>
          )}
        </div>
      </div>
      <Card className="teammate-toolbar" variant="borderless">
        <div>
          <strong>Team directory</strong>
          <span>Search by name, email, phone, or country</span>
        </div>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search teammates"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
        />
      </Card>
      {loading ? (
        <div className="teammate-loading">
          <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
        </div>
      ) : visibleGroups.length === 0 ? (
        <div className="teammate-empty">
          <Empty description="No active groups found" />
        </div>
      ) : (
        visibleGroups.map((group) => (
          <section className="team-group-section" key={group._id}>
            <div className="team-group-heading">
              <div>
                <span>Group owner</span>
                <strong>{group.owner?.name || group.name}</strong>
                <small>{group.owner?.email}</small>
              </div>
              <Tag>{group.members.length} teammates</Tag>
            </div>
            <div className="teammate-wrapper">
              {group.members.length === 0 ? (
                <div className="teammate-empty">
                  <Empty
                    description={
                      searchText
                        ? "No teammates match your search"
                        : "No teammates invited yet"
                    }
                  />
                </div>
              ) : (
                group.members.map((item) => (
                  <Card
                    className="teammate-item"
                    variant="borderless"
                    key={item._id}
                  >
                    <div className="teammate-card-main">
                      <div className="teammate-avatar-bar">
                        <img
                          className="teammate-avatar"
                          src={item.avatar}
                          alt=""
                        />
                        <span className="online-indicator" />
                      </div>
                      <div className="teammate-info">
                        <div className="teammate-name">{item.name}</div>
                        <Tag>{getCountryName(item.job_type)}</Tag>
                      </div>
                    </div>
                    <div className="teammate-details">
                      <p>
                        <span>Email</span>
                        <strong>{item.email}</strong>
                      </p>
                      <p>
                        <span>Phone</span>
                        <strong>{item.phone || "Not provided"}</strong>
                      </p>
                      <p>
                        <span>Sex</span>
                        <strong>
                          {String(item.gender) === "0"
                            ? "Male"
                            : String(item.gender) === "1"
                              ? "Female"
                              : "Not provided"}
                        </strong>
                      </p>
                      <div className="teammate-colors">
                        <span
                          title="Background color"
                          style={{
                            background: item.background_color || "#ffffff",
                          }}
                        />
                        <span
                          title="Text color"
                          style={{ background: item.text_color || "#10212b" }}
                        />
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </section>
        ))
      )}
      <Modal
        className="team-invite-modal"
        open={inviteOpen}
        footer={null}
        width={540}
        centered
        title={
          <div className="team-invite-title">
            <span>
              <UserAddOutlined />
            </span>
            <div>
              <strong>Invite a team member</strong>
              <small>Grow your Professional workspace</small>
            </div>
          </div>
        }
        onCancel={() => {
          setInviteOpen(false);
          setInviteResults([]);
          setInvitedIds([]);
          setInviteQuery("");
        }}
      >
        <div className="team-invite-search-block">
          <label htmlFor="team-invite-search">Find registered users</label>
          <p>Search by name, email address, or phone number.</p>
          <Input.Search
            id="team-invite-search"
            autoFocus
            size="large"
            value={inviteQuery}
            loading={inviteLoading}
            prefix={<SearchOutlined />}
            placeholder="e.g. Shawn or shawn@example.com"
            enterButton="Search"
            onChange={(event) => setInviteQuery(event.target.value)}
            onSearch={searchUsers}
          />
        </div>
        <div className="team-invite-results">
          {inviteLoading ? (
            <div className="team-invite-loading">
              <Spin />
              <span>Searching your workspace...</span>
            </div>
          ) : inviteResults.length > 0 ? (
            <>
              <div className="team-invite-results-label">
                <span>Search results</span>
                <strong>{inviteResults.length} found</strong>
              </div>
              {inviteResults.map((user) => {
                const invited = invitedIds.includes(user._id);
                return (
                  <div
                    className={`team-invite-user ${invited ? "is-invited" : ""}`}
                    key={user._id}
                  >
                    <div className="team-invite-user-profile">
                      <Avatar size={46} src={user.avatar}>
                        {user.name?.[0]}
                      </Avatar>
                      <div>
                        <strong>{user.name}</strong>
                        <span>Resume Builder member</span>
                      </div>
                    </div>
                    <Button
                      type={invited ? "default" : "primary"}
                      icon={invited ? <CheckOutlined /> : <UserAddOutlined />}
                      disabled={invited}
                      loading={invitingId === user._id}
                      onClick={() => inviteUser(user)}
                    >
                      {invited ? "Invited" : "Invite"}
                    </Button>
                  </div>
                );
              })}
            </>
          ) : inviteQuery.trim().length >= 2 ? (
            <div className="team-invite-empty">
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No matching users found"
              />
              <span>Try a different name, email, or phone number.</span>
            </div>
          ) : (
            <div className="team-invite-placeholder">
              <SearchOutlined />
              <strong>Search for your next teammate</strong>
              <span>Results will appear here after you press Enter.</span>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default TeamMates;
